import { Trans } from '@lingui/react/macro';
import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from '@repo/videa-components/src/ui/hover-card';

import type { Appointment } from '@/react-query/useAppointments';

import type { TVisitSummary } from '../../../../react-query/useVisitSummaries';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { useTreatmentsGroupedByStatus } from '../patientStripOdontogram/hooks';

import { SheetTxProceduresInfo } from './SheetTxProceduresInfo';

export const ScheduledTodayPill = ({
  appointment,
  visitSummary,
}: {
  appointment?: Appointment;
  visitSummary?: TVisitSummary;
}) => {
  const { trackEvent } = useTrackEvent();
  const treatmentsGroupedByStatus = useTreatmentsGroupedByStatus(visitSummary);
  const totalCount = treatmentsGroupedByStatus.scheduled_today.flatMap(
    ({ treatments }) => treatments,
  ).length;

  if (!totalCount) return null;

  return (
    <HoverCard
      openDelay={100}
      closeDelay={100}
      onOpenChange={(open) => {
        if (open) {
          trackEvent(mixpanelEvents.userHoverScheduledTodayPill, {
            totalCount,
            estimatedVisitId: visitSummary?.estimatedVisitId,
            unifiedId: visitSummary?.unifiedAppointmentId,
            pmsPatientId: appointment?.patient.pmsPatientId,
            pmsAppointmentId: appointment?.appointment.pmsAppointmentId,
            imagingPatientId: appointment?.patient.imagingPatientId,
          });
        }
      }}
    >
      <HoverCardTrigger className="vai-self-start">
        <div className="vai-bg-slate-200 vai-rounded-3xl vai-py-1 vai-px-2">
          <p className="vai-typography-small !vai-text-[12px]">
            <Trans>Tx Today</Trans>
          </p>
        </div>
      </HoverCardTrigger>
      {/* The HoverCardPortal ensures that the HoverCardContent is rendered at the root level of the DOM to avoid z-index issues. */}
      <HoverCardPortal>
        <HoverCardContent className="vai-w-max vai-text-slate-950 vai-font-sans vai-max-h-[50vh] vai-overflow-y-auto">
          {treatmentsGroupedByStatus.scheduled_today.map(
            ({ mouthArea, treatments }) => (
              <SheetTxProceduresInfo
                affectedArea={mouthArea}
                key={mouthArea}
                tx={treatments}
              />
            ),
          )}
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
};
